import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";
import ParamInputForm from "./ParamInputForm";
import FailedEndpointEditor from "./FailedEndpointEditor";

const METHOD_COLORS = {
    GET: "bg-green-500", POST: "bg-blue-500", PUT: "bg-amber-500", PATCH: "bg-orange-500",
    DELETE: "bg-red-500", QUERY: "bg-green-500", MUTATION: "bg-purple-500", SUBSCRIPTION: "bg-cyan-500",
};

export default function EndpointTestView({ endpoint, result, fix, onApprove, onSkip, autoApprove, onToggleAutoApprove, pendingParams, onSubmitParams, onRetest, onFixWithAI, isFixing }) {
    const { t } = useTranslation();
    if (!endpoint) return null;

    const isTesting = !result && !pendingParams;
    const hasFailed = result && result.status >= 400;
    const hasPassed = result && result.status < 400;

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b flex items-center gap-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded text-white ${METHOD_COLORS[endpoint.method] || "bg-gray-500"}`}>{endpoint.method}</span>
                    <span className="font-mono text-sm text-gray-800 truncate min-w-0" title={endpoint.path}>{endpoint.path}</span>
                    <span className="text-xs text-gray-400 ml-auto">{endpoint.description}</span>
                </div>

                {endpoint.query && (
                    <div className="px-5 py-3 border-b bg-gray-950">
                        <pre className="text-xs text-purple-300 font-mono whitespace-pre-wrap">{endpoint.query}</pre>
                    </div>
                )}

                {pendingParams && <ParamInputForm params={pendingParams.params} defaults={pendingParams.values} onSubmit={onSubmitParams} onSkip={onSkip} isGraphQL={["QUERY", "MUTATION", "SUBSCRIPTION"].includes(endpoint.method)} />}

                {isTesting && (
                    <div className="px-5 py-8 flex items-center justify-center gap-3 text-blue-600">
                        <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                        <span className="text-sm font-medium">{endpoint.query ? t("builder.test.sendingGraphql") : t("builder.test.sendingRequest")}</span>
                    </div>
                )}

                {result && (
                    <div className="px-5 py-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <StatusBadge status={result.status} />
                            <span className="text-xs text-gray-400">{result.ms}ms</span>
                        </div>
                        <div className="bg-gray-950 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-xs text-green-400 font-mono leading-relaxed">{JSON.stringify(result.body, null, 2)}</pre>
                        </div>
                        {hasFailed && <FailedEndpointEditor path={endpoint.path} onRetest={onRetest} onSkip={onSkip} onFixWithAI={onFixWithAI} isFixing={isFixing} status={result.status} />}
                    </div>
                )}

                {hasPassed && (
                    <div className="px-5 py-3 border-t bg-gray-50">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2 cursor-pointer select-none" onClick={onToggleAutoApprove}>
                                <div className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${autoApprove ? "bg-blue-600" : "bg-gray-300"}`}>
                                    <div className={`absolute top-[2px] left-[2px] w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${autoApprove ? "translate-x-4" : "translate-x-0"}`} />
                                </div>
                                <span className="text-xs text-gray-600">{t("builder.test.autoApprove")}</span>
                            </label>
                            <div className="flex gap-2">
                                <button onClick={onSkip} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-200 rounded-md transition-colors">{t("common.skip")}</button>
                                <button onClick={onApprove} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1">
                                    <Icon name="Play" className="w-3 h-3" /> {t("builder.test.approveNext")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    const { t } = useTranslation();
    const isOk = status < 400;
    return (
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${isOk ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
            {status} {isOk ? "OK" : t("common.error")}
        </span>
    );
}
